﻿import React, { memo, useEffect, useState, useRef } from 'react'
import { createStyles, withStyles } from '@mui/styles'
import { FormControl, InputLabel, Theme, ThemeProvider, createTheme, Box } from '@mui/material'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import { MuiProps, muiOptions, defaultColors } from '../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../infrastructure/i18nextHelper'
import { api } from '../../../infrastructure/api'
import { useContainer } from 'unstated-next'
import { CommentContainer } from './commentStore'

export type CommentContentProps = {
    associationKey: string
}

let theme: Theme = createTheme()

function _CommentContent(props: MuiProps & CommentContentProps) {
    let [commentContent, setCommentContent] = useState<string | null>(null)
    let store = useContainer(CommentContainer)
    let quillRef = useRef<ReactQuill>(null)

    useEffect(() => {
        if (!props.associationKey) return
        loadComment()
    }, [store.existingKeys, props.associationKey])

    let loadComment = async () => {
        let comment = await api.get<CommentItem | null>(`comment?associationKey=${props.associationKey}`)
        setComment(comment?.content)
    }

    let setComment = (content?: string | null) => {
        if (!content) {
            setCommentContent(null)
            return
        }

        let htmlContent = store.convertContentTextToHTMLString(content)
        setCommentContent(htmlContent)
    }

    return (
        <div className={props.classes.container}>
            <ThemeProvider theme={theme}>
                <FormControl variant="filled" fullWidth>
                    <InputLabel shrink>{t('comment.comment')}</InputLabel>
                    <Box
                        sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            borderRadius: 1,
                            marginTop: '18px',
                            '& .ql-editor': {
                                padding: '12px 12px 0px',
                                minHeight: '47px',
                                lineHeight: '1.1876em',
                                fontFamily: 'Roboto',
                                color: defaultColors.inputText,
                                border: 'none',
                            },
                            '& .ql-container': {
                                border: 'none',
                                fontSize: 'inherit',
                            },
                            '& .ql-toolbar': {
                                display: 'none', // Hide toolbar for read-only
                            }
                        }}
                    >
                        <ReactQuill
                            ref={quillRef}
                            value={commentContent || ''}
                            readOnly={true}
                            theme="snow"
                            preserveWhitespace={false}
                            modules={{ toolbar: false }}
                            formats={[]}
                        />
                    </Box>
                </FormControl>
            </ThemeProvider>
        </div>)
}

let styles = _ =>
    createStyles({
        container: {
            width: '100%',
            paddingRight: '0.5em',
            '& label': {
                color: 'rgba(0, 0, 0, 0.54) !important',
                fontSize: '0.75em !important',
                transform: 'translate(20px,10px) !important'
            }
        }
    })

export let CommentContent = withStyles(styles, muiOptions)(memo(_CommentContent))

type CommentItem = {
    associationKey: string
    content: string
    user: string
    createdAt: Date
}